// Generations browser — paginated list with status + photoshoot filters.
// Refund button on `failed` and `completed` rows; the backend enforces
// the refund-once invariant via `credits_used` + status check, we just
// surface the affordance.

import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { APIError, request } from '@/lib/api'
import { Field, firstApiError } from '@/components/FormControls'
import type {
  AdminGenerationListItem,
  AdminGenerationListResponse,
  AdminPhotoshootResponse,
  AdminRefundRequest,
  AdminRefundResponse,
  GenerationStatus,
} from '@/types/api'

const STATUS_FILTERS: { value: GenerationStatus | ''; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'queued', label: 'Queued' },
  { value: 'processing', label: 'Processing' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
]

export function GenerationsPage() {
  const queryClient = useQueryClient()
  const [status, setStatus] = useState<GenerationStatus | ''>('')
  const [photoshootId, setPhotoshootId] = useState('')
  const [cursor, setCursor] = useState<string | null>(null)
  // PX-430: "See full generation history →" links here with ?user_id=… —
  // read it so the list is actually scoped to that user (the backend route
  // already accepts the filter; the page just wasn't forwarding it).
  const [searchParams, setSearchParams] = useSearchParams()
  const userId = searchParams.get('user_id') ?? ''

  const photoshoots = useQuery({
    queryKey: ['admin', 'photoshoots'],
    queryFn: () => request<AdminPhotoshootResponse[]>('/admin/photoshoots'),
  })

  const listQuery = useQuery({
    queryKey: ['admin', 'generations', { status, photoshootId, userId, cursor }],
    queryFn: () =>
      request<AdminGenerationListResponse>('/admin/generations', {
        query: {
          status: status || undefined,
          photoshoot_id: photoshootId || undefined,
          user_id: userId || undefined,
          cursor: cursor ?? undefined,
          limit: 25,
        },
      }),
  })

  const refundMutation = useMutation({
    mutationFn: ({ id, amount, reason }: { id: string; amount: number; reason: string }) =>
      request<AdminRefundResponse>(`/admin/generations/${id}/refund`, {
        method: 'POST',
        body: { amount, reason } satisfies AdminRefundRequest,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'generations'] }),
  })

  return (
    <div className="p-6">
      <header className="mb-4">
        <h1 className="text-2xl font-semibold tracking-tight">Generations</h1>
        <p className="text-sm text-ink-400">Inspect runs, refund users when a generation lands wrong.</p>
      </header>

      {userId && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-brand-200 bg-brand-50 px-3 py-2 text-sm">
          <span className="text-ink-600">Filtered to user</span>
          <code className="rounded bg-white px-1.5 py-0.5 font-mono text-xs text-ink-700">{userId}</code>
          <button
            type="button"
            onClick={() => {
              searchParams.delete('user_id')
              setSearchParams(searchParams, { replace: true })
              setCursor(null)
            }}
            className="ml-1 rounded border border-ink-200 px-2 py-0.5 text-xs font-medium text-ink-600 transition hover:bg-white"
          >
            Clear
          </button>
        </div>
      )}

      {refundMutation.error instanceof APIError && (
        <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {refundMutation.error.message}
        </div>
      )}

      <div className="mb-3 flex flex-wrap gap-3">
        <div className="inline-flex gap-1 rounded-lg bg-ink-100 p-1">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value || 'all'}
              type="button"
              onClick={() => { setStatus(f.value); setCursor(null) }}
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition ${
                status === f.value ? 'bg-white text-ink-900 shadow-sm' : 'text-ink-500 hover:text-ink-900'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <select
          value={photoshootId}
          onChange={(e) => { setPhotoshootId(e.target.value); setCursor(null) }}
          className="rounded-md border border-ink-200 bg-white px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
        >
          <option value="">All photoshoots</option>
          {photoshoots.data?.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </div>

      <div className="overflow-x-auto rounded-xl border border-ink-100 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="border-b border-ink-100 bg-ink-50 text-left text-xs uppercase tracking-wide text-ink-400">
            <tr>
              <th className="px-4 py-3">Created</th>
              <th className="px-4 py-3">User</th>
              <th className="px-4 py-3">Photoshoot</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Credits</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {listQuery.isLoading && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-ink-400">Loading…</td></tr>
            )}
            {listQuery.data?.items.map((g) => (
              <GenerationRow
                key={g.id}
                gen={g}
                refunding={refundMutation.isPending}
                refundError={refundMutation.error instanceof APIError ? refundMutation.error : null}
                onRefund={(amount, reason) => refundMutation.mutate({ id: g.id, amount, reason })}
              />
            ))}
            {!listQuery.isLoading && listQuery.data?.items.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-ink-400">No generations match this filter.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {listQuery.data?.has_more && listQuery.data.cursor && (
        <div className="mt-4 text-right">
          <button
            type="button"
            onClick={() => setCursor(listQuery.data!.cursor!)}
            className="rounded-md border border-ink-200 px-3 py-1.5 text-sm font-medium text-ink-700 transition hover:border-ink-300"
          >
            Next page →
          </button>
        </div>
      )}
    </div>
  )
}

function StatusPill({ status }: { status: GenerationStatus }) {
  const tone: Record<GenerationStatus, string> = {
    queued: 'bg-ink-100 text-ink-500',
    processing: 'bg-amber-100 text-amber-800',
    completed: 'bg-emerald-100 text-emerald-800',
    failed: 'bg-red-100 text-red-800',
  }
  return (
    <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${tone[status]}`}>
      {status}
    </span>
  )
}

function GenerationRow({
  gen,
  refunding,
  refundError,
  onRefund,
}: {
  gen: AdminGenerationListItem
  refunding: boolean
  refundError: APIError | null
  onRefund: (amount: number, reason: string) => void
}) {
  const canRefund = gen.credits_used > 0
  const [showRefund, setShowRefund] = useState(false)
  return (
    <>
      <tr className="border-b border-ink-100 last:border-0 align-top">
        <td className="px-4 py-3 text-xs">
          <Link to={`/generations/${gen.id}`} className="text-brand-600 hover:underline">
            {new Date(gen.created_at).toLocaleString()}
          </Link>
        </td>
        <td className="px-4 py-3">
          <div className="text-ink-700">{gen.user_email ?? '—'}</div>
          <div className="font-mono text-[10px] text-ink-400">{gen.user_id}</div>
        </td>
        <td className="px-4 py-3 text-ink-700">{gen.photoshoot_name}</td>
        <td className="px-4 py-3">
          <StatusPill status={gen.status} />
          {gen.error_message && (
            <div className="mt-1 max-w-xs truncate text-xs text-red-600" title={gen.error_message}>{gen.error_message}</div>
          )}
        </td>
        <td className="px-4 py-3 tabular-nums">{gen.credits_used}</td>
        <td className="px-4 py-3 text-right">
          {canRefund && (
            <button
              type="button"
              disabled={refunding}
              onClick={() => setShowRefund(true)}
              className="rounded-md border border-ink-200 px-2.5 py-1 text-xs font-medium text-ink-700 transition hover:border-brand-300 hover:text-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Refund
            </button>
          )}
        </td>
      </tr>
      {showRefund && (
        <RefundModal
          maxAmount={gen.credits_used}
          pending={refunding}
          error={refundError}
          onConfirm={(amount, reason) => {
            onRefund(amount, reason)
            setShowRefund(false)
          }}
          onCancel={() => setShowRefund(false)}
        />
      )}
    </>
  )
}

// ----- Refund modal -------------------------------------------------
// Same visual pattern as RoleChangeConfirmModal / SubscriptionChangeConfirmModal
// in Users.tsx: fixed backdrop + max-w-md white card + Cancel / Confirm footer.

function RefundModal({
  maxAmount,
  pending,
  error,
  onConfirm,
  onCancel,
}: {
  maxAmount: number
  pending: boolean
  error?: APIError | null
  onConfirm: (amount: number, reason: string) => void
  onCancel: () => void
}) {
  const [amount, setAmount] = useState(String(maxAmount))
  const [reason, setReason] = useState('')

  const parsed = Number(amount)
  const amountValid = Number.isFinite(parsed) && Number.isInteger(parsed) && parsed >= 1 && parsed <= maxAmount
  const canSubmit = amountValid && !pending

  const apiErr = firstApiError(error)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-6" onClick={onCancel}>
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold tracking-tight">Refund credits?</h2>
        <p className="mt-2 text-sm text-ink-500">
          Enter how many credits to return. The backend enforces refund-once; this call simply surfaces the affordance.
        </p>

        {apiErr && (
          <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {apiErr.message}
          </div>
        )}

        <div className="mt-4 space-y-3">
          <Field
            label={`Amount (1–${maxAmount})`}
            type="number"
            value={amount}
            onChange={setAmount}
          />
          {!amountValid && amount !== '' && (
            <p className="text-xs text-red-600">Must be a whole number between 1 and {maxAmount}.</p>
          )}
          <Field
            label="Reason (audit log)"
            value={reason}
            onChange={setReason}
            placeholder="e.g. generation quality issue"
          />
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={pending}
            className="rounded-md border border-ink-200 px-3 py-1.5 text-sm font-medium text-ink-700 transition hover:bg-ink-50 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!canSubmit}
            onClick={() => onConfirm(parsed, reason.trim())}
            className="rounded-md bg-brand-500 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-brand-600 disabled:opacity-60"
          >
            {pending ? 'Refunding…' : `Refund ${amountValid ? parsed : ''}`.trim()}
          </button>
        </div>
      </div>
    </div>
  )
}
